import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  InvoiceValidationError,
  SkippedDetail,
  UploadDetails,
} from "@/types/uploadInvoice";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ColumnDef,
  ColumnFiltersState,
  Row,
  RowSelectionState,
} from "@tanstack/react-table";
import { format } from "date-fns";
import { useState } from "react";
import DataTable from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import {
  CheckCircle,
  CircleAlert,
  Edit3,
  Eye,
  Loader2,
  Upload,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { navigate } from "wouter/use-browser-location";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { updateFileSchema, UpdateUploadFormData } from "@/schema/upload.schema";
import { useParams } from "wouter";
import { useImport, useImportDetails } from "@/hooks/useImport";
import LoadingIndicator from "@/components/LoadingIndicator";
import { getInvoiceTypeDescription } from "@/utils/helper/invoice.mapping.helper";
import { AxiosError } from "axios";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { importInvoiceStatus } from "@/constants/invoiceTypes";

const getColumns = (
  onRowAction: (id: string) => void,
): ColumnDef<UploadDetails>[] => [
  {
    id: "rowNumber",
    header: () => (
      <span className="w-full flex justify-center font-semibold">No.</span>
    ),
    cell: ({ row, table }) => {
      const { pageIndex, pageSize } = table.getState().pagination;

      const pageRows = table.getPaginationRowModel().rows;

      const rowIndexInPage = pageRows.findIndex(
        (pageRow) => pageRow.id === row.id,
      );

      const rowNumber = pageIndex * pageSize + rowIndexInPage + 1;

      return (
        <span className="w-full flex items-center justify-center font-semibold">
          {rowNumber}
        </span>
      );
    },
  },
  {
    accessorKey: "posReferenceNumber",
    header: "POS Reference No.",
    cell: ({ row }) => (
      <span className="font-medium">
        {row.original.posReferenceNumber || "—"}
      </span>
    ),
  },
  {
    accessorKey: "invoiceType",
    header: "Invoice Type",
    cell: ({ row }) => {
      return getInvoiceTypeDescription(row.original.invoiceType);
    },
  },
  {
    accessorKey: "invoiceDate",
    header: "Invoice Date",
    cell: ({ row }) => {
      const date = row.original.invoiceDate;
      try {
        return format(new Date(date), "dd/MM/yyyy");
      } catch {
        return date;
      }
    },
  },
  {
    accessorKey: "buyerName",
    header: "Buyer",
  },
  {
    accessorKey: "buyerEmail",
    header: "Buyer Email",
  },
  {
    accessorKey: "supplierName",
    header: "Supplier",
  },
  {
    accessorKey: "supplierEmail",
    header: "Supplier Email",
  },
  {
    accessorKey: "totalExcludingTax",
    header: "Total Excluding Tax",
  },
  {
    accessorKey: "totalIncludingTax",
    header: "Total Including Tax",
  },
  {
    accessorKey: "isError",
    header: "Validation Status",
    cell: ({ row }) => {
      return (
        <span
          className={`px-2 rounded-md ${
            row.original.isError
              ? "bg-red-100 text-red-800"
              : "bg-green-100 text-green-800"
          }`}
        >
          {row.original.isError ? "Failed" : "Passed"}
        </span>
      );
    },
    filterFn: (
      row: Row<UploadDetails>,
      columnId: string,
      filterValue: string,
    ) => {
      const isError = row.getValue(columnId) as boolean;

      //return both value if all
      if (filterValue === "All") return true;

      //convert string value from dropdown
      if (filterValue === "true") {
        return isError === true; //show failed invoices
      } else if (filterValue === "false") {
        return isError === false; //show passed invoices
      }

      return true; //show all if unexpected occur
    },
  },
  {
    id: "select",
    header: "",
    cell: ({ row, table }) => {
      const uploadStatus = window.history.state?.status ?? null;
      return (
        <Button
          variant="ghost"
          size="sm"
          disabled={uploadStatus === "In Progress"}
          onClick={() => {
            table.setRowSelection({ [row.id]: true });
            onRowAction(row.original.id); //event to call modal for selected row
          }}
          aria-label={`View details for ${row.original.id}`}
        >
          <Eye />
        </Button>
      );
    },
  },
];

const ImportDetailsPage = () => {
  const { toast } = useToast();
  const importId = useParams().id as string;
  const [selectedRowId, setSelectedRowId] = useState<string>("");
  const {
    importDetails,
    skippedDetails,
    isLoading,
    invoiceValidationDetails,
    isLoadingValidation,
  } = useImportDetails(importId, selectedRowId);
  const { updateFile, isUpdating } = useImport();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [isOpen, setIsOpen] = useState(false);
  const uploadStatus = window.history.state?.status ?? null;
  const [isAmendOpen, setIsAmendOpen] = useState(false);

  // Derive the real pass/fail summary from the actual invoice rows
  const isInProgress = uploadStatus === "In Progress";
  const totalInvoices = (importDetails ?? []).length;
  const failedInvoices = (importDetails ?? []).filter((d) => d.isError).length;
  const hasFailures = failedInvoices > 0;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UpdateUploadFormData>({
    resolver: zodResolver(updateFileSchema),
    defaultValues: {
      file: new DataTransfer().files,
    },
  });

  const handleRowAction = (id: string) => {
    setSelectedRowId(id);
    setIsOpen(true);
  };

  const onSubmit = async (data: UpdateUploadFormData) => {
    await updateFile(
      { formData: data, uploadId: importId },
      {
        onSuccess: () => {
          toast({
            title: "File Uploaded Successfully",
            description: `The file for the import has been updated.`,
            duration: 5000,
            icon: (
              <CheckCircle
                size={30}
                className="animate-spin-once text-green-400"
              />
            ),
          });

          setIsAmendOpen(false);
          reset();
          navigate("/import/records");
        },
        onError: (error) => {
          let errorMessage = "Something went wrong. Please try again later.";
          if (error instanceof AxiosError) {
            errorMessage = error.response?.data.message;
          }
          toast({
            title: "Upload Failed",
            description: errorMessage,
            variant: "destructive",
            icon: <CircleAlert size={30} className="animate-spin-once" />,
            duration: 4000,
          });
        },
      },
    );

    toast({
      title: "File Uploaded Successfully",
      description: `The import file has been uploaded.`,
      duration: 5000,
      icon: (
        <CheckCircle size={30} className="animate-spin-once text-green-400" />
      ),
    });
  };

  const statusFilter = columnFilters.find((f) => f.id === "isError");
  const statusFilterValue = (statusFilter?.value as string) || "";

  const handleStatusChange = (selectedStatus: string) => {
    setColumnFilters((prev) => {
      return [
        ...prev.filter((filter) => filter.id !== "isError"),
        ...(selectedStatus !== "All"
          ? [{ id: "isError", value: selectedStatus }]
          : []),
      ];
    });
  };

  const maxLabelLength = Math.max(
    ...importInvoiceStatus.map((status) => status.label.length),
  );
  const minWidth = Math.max(100, maxLabelLength * 8 + 60);

  return (
    <div className="min-h-screen flex items-start justify-center p-4">
      <div className="w-full space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>Import Details</CardTitle>
            <CardDescription>
              View summary of validation results for all invoices
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="w-full flex flex-col gap-4">
              <div
                className={`w-full flex items-center justify-between p-2.5 text-sm rounded-md select-none ${
                  isInProgress
                    ? "bg-orange-100 border border-orange-200 text-orange-600"
                    : hasFailures
                      ? "bg-red-50 border border-red-100 text-red-700 dark:bg-red-950/20 dark:border-red-900/50 dark:text-red-400"
                      : "bg-green-50 border border-green-100 text-green-700 dark:bg-green-950/20 dark:border-green-900/50 dark:text-green-400"
                }`}
              >
                <span className="font-semibold">
                  Status:{" "}
                  {isInProgress
                    ? "In Progress"
                    : hasFailures
                      ? `${failedInvoices} of ${totalInvoices} invoice(s) failed validation`
                      : `All ${totalInvoices} invoice(s) passed validation`}
                </span>
                {!isInProgress && hasFailures && (
                  <Button
                    size="sm"
                    type="button"
                    onClick={() => setIsAmendOpen(true)}
                  >
                    <Edit3 /> Amend
                  </Button>
                )}
              </div>
              {isLoading ? (
                <LoadingIndicator />
              ) : (
                <>
                  <div className="w-full flex justify-end px-3">
                    <div className="flex gap-3 items-center">
                      <Label>Status</Label>
                      <Select
                        onValueChange={handleStatusChange}
                        value={statusFilterValue}
                      >
                        <SelectTrigger style={{ minWidth: `${minWidth}px` }}>
                          <SelectValue placeholder="All" />
                        </SelectTrigger>
                        <SelectContent>
                          {importInvoiceStatus.map((status) => (
                            <SelectItem key={status.label} value={status.value}>
                              {status.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DataTable<UploadDetails>
                    data={importDetails || []}
                    columns={getColumns(handleRowAction)}
                    rowSelection={rowSelection}
                    setRowSelection={setRowSelection}
                    columnFilters={columnFilters}
                    setColumnFilters={setColumnFilters}
                    showSelectedIndicator={false}
                  />
                  {skippedDetails && skippedDetails.length > 0 && (
                    <div className="w-full flex flex-col gap-2 mt-2">
                      <p className="text-sm font-semibold text-orange-700 dark:text-orange-400">
                        Skipped ({skippedDetails.length}) — Original invoice not
                        found
                      </p>
                      <div className="w-full border border-orange-200 dark:border-orange-900/50 rounded-md overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-orange-50 dark:bg-orange-950/20 text-orange-700 dark:text-orange-400">
                            <tr>
                              <th className="text-left px-3 py-2 font-semibold">
                                Row
                              </th>
                              <th className="text-left px-3 py-2 font-semibold">
                                POS Reference Number
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {skippedDetails.map(
                              (detail: SkippedDetail, idx: number) => (
                                <tr
                                  key={idx}
                                  className="border-t border-orange-100 dark:border-orange-900/30 even:bg-orange-50/50 dark:even:bg-orange-950/10"
                                >
                                  <td className="px-3 py-2 text-slate-600 dark:text-slate-400">
                                    {detail.row}
                                  </td>
                                  <td className="px-3 py-2 text-slate-600 dark:text-slate-400">
                                    {detail.posReferenceNumber || "—"}
                                  </td>
                                </tr>
                              ),
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Invoice Details */}
            <Dialog open={isOpen} onOpenChange={setIsOpen}>
              <DialogContent className="sm:max-w-3xl h-fit max-h-[90vh] flex flex-col">
                <DialogHeader>
                  <DialogTitle>Invoice Details</DialogTitle>
                  <DialogDescription>
                    View validation results for selected invoice
                  </DialogDescription>
                </DialogHeader>
                <div className="w-full flex-1 grid overflow-y-auto text-sm p-1.5">
                  {isLoadingValidation ? (
                    <LoadingIndicator />
                  ) : invoiceValidationDetails?.validationError ? (
                    <div className="w-full flex flex-col gap-1">
                      {invoiceValidationDetails.validationError.map(
                        (error: InvoiceValidationError, index: number) => (
                          <div
                            key={index}
                            className="w-full flex flex-col gap-2.5 bg-red-100 text-red-800 border border-red-200 p-2.5 rounded-md select-none"
                          >
                            <p className="text-sm font-bold">
                              {error.field}:{" "}
                              <span className="font-normal">
                                {error.message}
                              </span>
                            </p>
                          </div>
                        ),
                      )}
                    </div>
                  ) : (
                    <div className="w-full flex gap-2.5 items-center bg-green-100 text-green-800 border border-green-200 p-2.5 rounded-md select-none">
                      <CheckCircle />
                      Invoice passed validation
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>

            {/* Amend Invoice */}
            <Dialog open={isAmendOpen} onOpenChange={setIsAmendOpen}>
              <DialogContent className="sm:max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Amend Import</DialogTitle>
                  <DialogDescription>
                    Upload a new file to replace the previous upload
                  </DialogDescription>
                </DialogHeader>
                <div className="w-full bg-orange-50 text-sm border border-orange-100 text-slate-600 dark:bg-orange-950/20 dark:border-orange-900/50 dark:text-slate-400 p-2.5 rounded-md select-none">
                  <p className="font-bold">
                    Note:{" "}
                    <span className="font-normal">
                      Replacing file will overwrite all the previous validation
                      results and invoices. Please ensure the new file is
                      corrected. Maximum allowed file size is 5MB.
                    </span>
                  </p>
                </div>
                <form
                  className="w-full h-fit flex gap-4 items-center justify-center text-sm"
                  onSubmit={handleSubmit(onSubmit)}
                >
                  <Input
                    id="file"
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    type="file"
                    {...register("file")}
                    errors={!!errors.file}
                    disabled={isUpdating}
                  />

                  <Button size="sm" type="submit" disabled={isUpdating}>
                    {isUpdating ? (
                      <>
                        <Loader2
                          size={10}
                          className="animate-spin text-primary"
                        />
                        Uploading
                      </>
                    ) : (
                      <>
                        <Upload /> Upload
                      </>
                    )}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
export default ImportDetailsPage;
